import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import type { Workspace } from "@salon-pos/shared";
import { errorText, useToast } from "../ui";
import { linkAllowed, notifyApplied, type AiPanelMode } from "./aiBus";

/**
 * Task K: the floating "AI 助手" button of the owner / manager portal.
 *  - Help (everywhere): answers from the product help articles; without an AI key it is a plain search
 *    ("没有连接 AI，以下是相关说明"), with a key a conversational answer that cites the articles. Read-only.
 *  - Website design (in the website editor) and Customer import (Customers → Import): the assistant only PROPOSES;
 *    each proposal shows a before → after preview and is written only by "应用" (Apply), with Undo.
 * Links ("打开该页面") are filtered by the user's role and add-ons.
 */
interface Article { id: string; title: string; route: string; links: string[]; sections: Array<{ heading: string; text: string }>; best: number; addonRequired: boolean }
interface Proposal { id: string; kind: string; summary: string; preview: Array<{ op: string; target: string; before?: string; after?: string }> }
type Msg =
  | { role: "user"; text: string }
  | { role: "assistant"; text: string; articles?: Article[]; proposals?: Proposal[]; usd?: number }
  | { role: "search"; articles: Article[] };
interface Status { featureEnabled: boolean; connected: boolean; provider: string | null; model: string | null; capReached: boolean; canWebsite: boolean; canImport: boolean; canConfigure: boolean }

/** Minimal markdown for help text: numbered / bulleted lists, **bold**, paragraphs. */
export function HelpText({ text }: { text: string }) {
  const bold = (s: string) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>));
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => { if (list) { const L = list.ordered ? "ol" : "ul"; blocks.push(<L key={blocks.length}>{list.items.map((x, i) => <li key={i}>{bold(x)}</li>)}</L>); list = null; } };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const m = /^(\d+)[.)]\s+(.*)$/.exec(line) ?? /^([-*])\s+(.*)$/.exec(line);
    if (m) { const ordered = /\d/.test(m[1]!); if (!list || list.ordered !== ordered) { flush(); list = { ordered, items: [] }; } list.items.push(m[2]!); continue; }
    flush();
    if (line) blocks.push(<p key={blocks.length}>{bold(line)}</p>);
  }
  flush();
  return <div className="ai-md">{blocks}</div>;
}

export function AiAssistant({ ws }: { ws: Workspace }) {
  const { me } = useAuth();
  const { t, locale } = useI18n();
  const toast = useToast();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AiPanelMode>("help");
  const [importId, setImportId] = useState<string | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const [threads, setThreads] = useState<Record<AiPanelMode, Msg[]>>({ help: [], website: [], import: [] });
  const [conv, setConv] = useState<Record<AiPanelMode, string | undefined>>({ help: undefined, website: undefined, import: undefined });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Record<string, { state: "applied" | "discarded" | "undone"; undo?: Record<string, unknown> }>>({});
  const endRef = useRef<HTMLDivElement>(null);
  // platform switch SHOP_AI_ENABLED (me.shopAi): off = no shop AI helper at all
  const allowed = ws === "owner" && !!me && !!me.shopAi && (me.permissions["ai.use"] ?? "none") !== "none";

  const loadStatus = useCallback(async () => { try { setStatus(await api<Status>("/api/ai/status")); } catch { setStatus(null); } }, []);
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ mode: AiPanelMode; importId?: string }>).detail;
      setMode(d.mode); if (d.importId) setImportId(d.importId); setOpen(true);
    };
    window.addEventListener("spos:ai-open", h);
    return () => window.removeEventListener("spos:ai-open", h);
  }, []);
  useEffect(() => { if (open) void loadStatus(); }, [open, loadStatus]);
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: "end" }); }, [threads, busy]);
  if (!allowed || !me) return null;

  const onWebsite = loc.pathname.startsWith("/owner/website");
  const onImport = loc.pathname.startsWith("/owner/customers/import");
  const modes: AiPanelMode[] = ["help", ...(status?.canWebsite && (onWebsite || mode === "website") ? ["website" as const] : []), ...(status?.canImport && importId && (onImport || mode === "import") ? ["import" as const] : [])];
  const msgs = threads[mode];
  const push = (m: AiPanelMode, msg: Msg) => setThreads((x) => ({ ...x, [m]: [...x[m], msg] }));

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const m = mode;
    setInput(""); push(m, { role: "user", text }); setBusy(true);
    try {
      if (m === "help") {
        const r = await api<{ mode: "ai" | "search"; answer: string | null; articles: Article[]; conversationId?: string; usage?: { estimatedUsd: number } }>("/api/ai/help", { method: "POST", body: { question: text, lang: locale, conversationId: conv.help } });
        if (r.conversationId) setConv((c) => ({ ...c, help: r.conversationId }));
        push(m, r.mode === "ai" && r.answer ? { role: "assistant", text: r.answer, articles: r.articles, usd: r.usage?.estimatedUsd } : { role: "search", articles: r.articles });
      } else {
        const r = await api<{ conversationId: string; reply: string; proposals: Proposal[]; usage: { estimatedUsd: number } }>("/api/ai/chat", { method: "POST", body: { mode: m, message: text, lang: locale, conversationId: conv[m], ...(m === "import" && importId ? { importId } : {}) } });
        setConv((c) => ({ ...c, [m]: r.conversationId }));
        push(m, { role: "assistant", text: r.reply, proposals: r.proposals, usd: r.usage.estimatedUsd });
      }
    } catch (e) { push(m, { role: "assistant", text: errorText(e) }); } finally { setBusy(false); }
  };

  const act = async (p: Proposal, what: "apply" | "discard" | "undo") => {
    try {
      if (what === "undo" && p.kind === "website") {
        const vid = done[p.id]?.undo?.restoreVersionId;
        if (typeof vid === "string") await api("/api/website/versions/" + vid + "/restore", { method: "POST" });
      } else {
        const r = await api<{ undo?: Record<string, unknown> }>("/api/ai/proposals/" + p.id + "/" + what, { method: "POST" });
        if (what === "apply") { setDone((d) => ({ ...d, [p.id]: { state: "applied", undo: r.undo } })); notifyApplied(p.kind); toast(t("ai.applied")); return; }
      }
      setDone((d) => ({ ...d, [p.id]: { state: what === "undo" ? "undone" : "discarded" } }));
      if (what === "undo") { notifyApplied(p.kind); toast(t("ai.undone")); }
    } catch (e) { toast(errorText(e), "error"); }
  };

  const Articles = ({ list }: { list: Article[] }) => (
    <div className="ai-articles">
      {list.map((a) => {
        const links = a.links.filter((l) => linkAllowed(me, l));
        const sec = a.sections[a.best] ?? a.sections[0];
        return (
          <details key={a.id} className="ai-article" open={list.length === 1 || a === list[0]}>
            <summary><strong>{a.title}</strong></summary>
            {sec && <>{sec.heading && <div className="ai-sec-h">{sec.heading}</div>}<HelpText text={sec.text} /></>}
            {a.sections.length > 1 && <details className="ai-more"><summary>{a.sections.filter((x) => x !== sec).map((x) => x.heading).join(" · ")}</summary>{a.sections.filter((x) => x !== sec).map((x, i) => <div key={i}><div className="ai-sec-h">{x.heading}</div><HelpText text={x.text} /></div>)}</details>}
            <div className="row ai-links">
              {a.addonRequired && <span className="muted">{t("ai.addonRequired", { nav: t("nav.addons") })}</span>}
              {(a.addonRequired ? [] : links).slice(0, 3).map((l) => <Link key={l} className="btn btn-quiet btn-sm" to={l} onClick={() => setOpen(false)}>{t("ai.openPage")} → {l.split("?")[0]!.split("/").pop()}</Link>)}
            </div>
          </details>
        );
      })}
    </div>
  );

  const needsKey = mode !== "help" && status && !status.connected;
  return (
    <>
      <button type="button" className="ai-fab" onClick={() => { setMode(onWebsite && status?.canWebsite !== false ? (mode === "import" ? "help" : mode) : mode); setOpen((o) => !o); }} aria-expanded={open}>✨ {t("ai.button")}</button>
      {open && (
        <aside className="ai-panel" role="dialog" aria-label={t("ai.button")}>
          <header className="ai-head">
            <strong>✨ {t("ai.button")}</strong>
            <div className="ai-tabs" role="tablist">
              {modes.map((m) => <button key={m} role="tab" aria-selected={mode === m} className={"chip" + (mode === m ? " active" : "")} onClick={() => setMode(m)}>{t(("ai.mode." + m) as "ai.mode.help")}</button>)}
            </div>
            <button className="btn btn-quiet btn-sm" onClick={() => { setThreads((x) => ({ ...x, [mode]: [] })); setConv((c) => ({ ...c, [mode]: undefined })); }}>{t("ai.newChat")}</button>
            <button className="btn btn-quiet btn-sm" aria-label={t("ai.close")} onClick={() => setOpen(false)}>✕</button>
          </header>
          <div className="ai-body">
            {mode === "help" && <p className="muted small">{t("ai.readOnly")}</p>}
            {mode === "website" && <p className="muted small">{t("ai.draftOnly")}</p>}
            {mode === "import" && <p className="muted small">{t("ai.imp.consentNote")}</p>}
            {needsKey && <div className="note">{t("ai.connectFirst")} {status?.canConfigure ? <Link to="/owner/settings?tab=ai" onClick={() => setOpen(false)}>{t("ai.goSettings")}</Link> : t("ai.askOwner")}</div>}
            {msgs.map((m, i) => (
              <div key={i} className={"ai-msg ai-" + m.role}>
                {m.role === "user" && <div className="ai-bubble">{m.text}</div>}
                {m.role === "search" && (<>
                  <div className="ai-bubble">{m.articles.length ? t("ai.noKey") : t("ai.noResults")}</div>
                  <Articles list={m.articles} />
                </>)}
                {m.role === "assistant" && (<>
                  {m.text && <div className="ai-bubble"><HelpText text={m.text} /></div>}
                  {m.articles && m.articles.length > 0 && <><div className="muted small">{t("ai.sources")}</div><Articles list={m.articles} /></>}
                  {m.proposals?.map((p) => (
                    <div key={p.id} className="ai-proposal card">
                      <strong>{p.summary}</strong>
                      <div className="muted small">{t("ai.preview")}</div>
                      <table className="table ai-diff"><tbody>{p.preview.slice(0, 40).map((x, k) => <tr key={k}><td>{x.target}</td><td className="muted">{x.before ?? ""}</td><td>→ {x.after ?? ""}</td></tr>)}</tbody></table>
                      <div className="row">
                        {!done[p.id] && <><button className="btn btn-primary btn-sm" onClick={() => void act(p, "apply")}>{t("ai.apply")}</button><button className="btn btn-quiet btn-sm" onClick={() => void act(p, "discard")}>{t("ai.discard")}</button></>}
                        {done[p.id]?.state === "applied" && <><span className="pill pill-ok">{t("ai.applied")}</span><button className="btn btn-quiet btn-sm" onClick={() => void act(p, "undo")}>{t("ai.undo")}</button></>}
                        {done[p.id]?.state === "discarded" && <span className="pill pill-muted">{t("ai.discarded")}</span>}
                        {done[p.id]?.state === "undone" && <span className="pill pill-muted">{t("ai.undone")}</span>}
                      </div>
                    </div>
                  ))}
                  {typeof m.usd === "number" && m.usd > 0 && <div className="muted small">{t("ai.cost", { usd: m.usd.toFixed(2) })}</div>}
                </>)}
              </div>
            ))}
            {busy && <div className="muted">{t("ai.thinking")}</div>}
            <div ref={endRef} />
          </div>
          <form className="ai-input" onSubmit={(e) => { e.preventDefault(); void send(); }}>
            <textarea rows={2} value={input} maxLength={4000} placeholder={t(("ai.ph." + mode) as "ai.ph.help")} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} disabled={!!needsKey} />
            <button className="btn btn-primary" type="submit" disabled={busy || !input.trim() || !!needsKey}>{t("ai.send")}</button>
          </form>
        </aside>
      )}
    </>
  );
}
